import { useEffect, useRef } from 'react';
import type { InputHTMLAttributes } from 'react';

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  // Native HTML doesn't expose `indeterminate` as an attribute — only as a property — so
  // tri-state checkboxes need this prop + a ref + useEffect to set it imperatively.
  indeterminate?: boolean;
}

// Small token-styled checkbox. Default browser checkbox lives inside a 16px box with
// proper indeterminate support — useful for the bulk-select header (all / some / none).
// Stays close to the OS look so it doesn't feel out of place next to native form controls,
// but the box + accent colour are token-driven so it inherits the active theme.
export function Checkbox({ indeterminate, className = '', ...rest }: CheckboxProps) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = Boolean(indeterminate);
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      className={
        'h-4 w-4 cursor-pointer rounded-sm border-line-3 text-accent accent-[var(--accent)] focus:ring-1 focus:ring-accent disabled:cursor-not-allowed disabled:opacity-50 ' +
        className
      }
      {...rest}
    />
  );
}
